<!-- Search bar and overlay -->
<div cdkOverlayOrigin #origin="cdkOverlayOrigin">
	<lvr-search
		[debounceTime]="350"
		(changed)="onTerm($event)"
		[ariaLabel]="'global_search.title' | transloco"
		(keydown.tab)="focusFirstOverlayElement($event)"
		(keydown.arrowDown)="focusFirstOverlayElement($event)"
	></lvr-search>

	<!-- Overlay content -->
	<ng-template
		cdkConnectedOverlay
		[cdkConnectedOverlayOrigin]="origin"
		[cdkConnectedOverlayOpen]="panelOpen()"
		[cdkConnectedOverlayPositions]="overlayPositions"
		[cdkConnectedOverlayWidth]="origin.elementRef.nativeElement.offsetWidth"
		[cdkConnectedOverlayHasBackdrop]="false"
		[cdkConnectedOverlayPush]="true"
		[cdkConnectedOverlayFlexibleDimensions]="true"
		[cdkConnectedOverlayViewportMargin]="8"
		(overlayOutsideClick)="onOutsideClick($event)"
		[cdkConnectedOverlayPanelClass]="'lvr-search-overlay-panel'"
		(detach)="closePanel()"
	>
		<div
			#overlayContainer
			class="overlay"
			role="dialog"
			aria-modal="false"
			[attr.aria-label]="'global_search.results' | transloco"
			(keydown.escape)="closePanelAndRestoreFocus()"
			(keydown.tab)="handleOverlayTab($event)"
		>
			<!-- Filters -->
			<mat-accordion class="filters-accordion" displayMode="flat" multi>
				<mat-expansion-panel class="filters-panel">
					<mat-expansion-panel-header (keydown.arrowDown)="focusFirstResult($event)">
						<mat-panel-title>
							<mat-icon class="material-symbols-outlined" aria-hidden="true">filter_alt</mat-icon>
							{{ 'global_search.filters' | transloco }}
						</mat-panel-title>
					</mat-expansion-panel-header>

					<div class="filters-content" role="group" [attr.aria-label]="'global_search.filters' | transloco">
						@for (filter of filterOptions(); track filter.scope) {
							<mat-checkbox
								class="filter-option"
								[checked]="filter.checked()"
								(change)="toggleScope(filter, $event.checked)"
							>
								{{ filter.labelKey | transloco }}
							</mat-checkbox>
						}
					</div>
				</mat-expansion-panel>
			</mat-accordion>

			<!-- State -->
			@if (searchTermTooLong()) {
				<div class="state error" role="alert">
					{{ 'global_search.max_length_exceeded' | transloco: { max: maxSearchLength } }}
				</div>
			} @else if (hasSearchError()) {
				<div class="state error" role="alert">
					{{ 'global_search.search_error' | transloco }}
				</div>
			}

			@if (isLoading()) {
				<div class="state" role="status" aria-live="polite">
					{{ 'global_search.searching' | transloco }}
				</div>
			}

			@if (!isLoading() && !hasGroupedResults() && !searchTermTooLong() && !hasSearchError()) {
				<div class="state" role="status" aria-live="polite">
					{{ 'global_search.no_results' | transloco: { term: searchTerm() } }}
				</div>
			}

			<!-- Results -->
			@if (hasGroupedResults()) {
				@for (group of groupedResults(); track group.key) {
					<div class="group">
						<div class="group-header">
							{{ group.titleKey | transloco }}
						</div>

						<div
							class="results"
							infiniteScroll
							[infiniteScrollDistance]="1"
							[infiniteScrollThrottle]="150"
							[infiniteScrollDisabled]="
								group.isLoadingMore || group.loadMoreError || !group.hasMore || group.capReached
							"
							[infiniteScrollContainer]="'.overlay'"
							[fromRoot]="true"
							[scrollWindow]="false"
							(scrolled)="loadMore(group.category!)"
						>
							<mat-list>
								<!-- HERE ARE THE SUGGESTIONS -->
								@for (item of group.items; track item.item.id) {
									@if (item.personType === PERSON_TYPE.CLIENT) {
										<mat-list-item
											class="item result-item"
											role="button"
											tabindex="0"
											(click)="selectResult(item)"
											(keydown.enter)="selectResult(item)"
											(keydown.space)="selectResult(item); $event.preventDefault()"
											(keydown.arrowDown)="focusNextResult($event)"
											(keydown.arrowUp)="focusPreviousResult($event)"
											[attr.aria-label]="getResultAriaLabel(item)"
										>
											<span matListItemTitle>
												@if (item.item.firstName) {
													{{ item.item.name }}, {{ item.item.firstName }}
												} @else {
													{{ item.item.name }}
												}
											</span>

											<span matListItemLine>
												{{ 'global_search.born' | transloco }}
												{{
													isValidDate(item.item.dateOfBirth)
														? (item.item.dateOfBirth | date: dateDisplayFormat)
														: ('global_search.unknown' | transloco)
												}}
												| {{ ('global_search.case_owner' | transloco) + ' ' + (item.item.user?.name ?? '/') }}
											</span>

											@if (item.item.archiveId || item.item.isArchived || canActivateRepresentation(item)) {
												<div matListItemMeta class="result-chips" aria-hidden="true">
													@if (item.item.archiveId || item.item.isArchived) {
														<lvr-chip [icon]="'assignment'" [name]="'global_search.completed' | transloco"></lvr-chip>
														<lvr-chip [icon]="'inventory_2'" [name]="'global_search.archive' | transloco"></lvr-chip>
													}

													@if (canActivateRepresentation(item)) {
														<lvr-chip [icon]="'groups'" [name]="'global_search.representation' | transloco"></lvr-chip>
													}
												</div>
											}
										</mat-list-item>
									} @else if (item.personType === PERSON_TYPE.INSTITUTION_PERSON) {
										<mat-list-item
											class="item result-item"
											role="button"
											tabindex="0"
											(click)="selectResult(item)"
											(keydown.enter)="selectResult(item)"
											(keydown.space)="selectResult(item); $event.preventDefault()"
											(keydown.arrowDown)="focusNextResult($event)"
											(keydown.arrowUp)="focusPreviousResult($event)"
											[attr.aria-label]="getResultAriaLabel(item)"
										>
											<span matListItemTitle>{{ item.item.firstName }} {{ item.item.name }}</span>
											@if (item.item.institutionName) {
												<span matListItemLine>{{ item.item.institutionName }}</span>
											}
										</mat-list-item>
									} @else if (item.personType === PERSON_TYPE.USER) {
										<mat-list-item
											class="item result-item"
											role="button"
											tabindex="0"
											(click)="selectResult(item)"
											(keydown.enter)="selectResult(item)"
											(keydown.space)="selectResult(item); $event.preventDefault()"
											(keydown.arrowDown)="focusNextResult($event)"
											(keydown.arrowUp)="focusPreviousResult($event)"
											[attr.aria-label]="getResultAriaLabel(item)"
										>
											<span matListItemTitle>{{ item.item.name }}, {{ item.item.firstName }}</span>
											<span matListItemLine>{{ item.item.integrationServiceOffice?.remark }}</span>
										</mat-list-item>
									} @else if (
										item.personType === PERSON_TYPE.SPONSOR ||
										item.personType === PERSON_TYPE.INSTITUTION ||
										item.personType === PERSON_TYPE.INSTITUTION_BRANCH ||
										item.personType === PERSON_TYPE.INTEGRATION_SERVICE_OFFICE
									) {
										<mat-list-item
											class="item result-item"
											role="button"
											tabindex="0"
											(click)="selectResult(item)"
											(keydown.enter)="selectResult(item)"
											(keydown.space)="selectResult(item); $event.preventDefault()"
											(keydown.arrowDown)="focusNextResult($event)"
											(keydown.arrowUp)="focusPreviousResult($event)"
											[attr.aria-label]="getResultAriaLabel(item)"
										>
											<span matListItemTitle
												>{{ item.item.name ? item.item.name : item.item.remark }}
												<span class="text-sm">
													{{ institutionTypeLabel(item) | transloco }}
												</span>
											</span>
											<span matListItemLine>
												@if (item.item.address?.location || item.item.address?.postalCode) {
													@let address = item.item.address!;
													{{ address.street }} {{ address.housenumber }} - {{ address.postalCode }}
													{{ address.location }}
												} @else {
													{{ 'global_search.unknown' | transloco }}
												}
											</span>
										</mat-list-item>
									} @else if (item.personType === PERSON_TYPE.ASSIGNMENT) {
										@let title = getAssignmentTitle(item);
										<mat-list-item
											class="item result-item"
											role="button"
											tabindex="0"
											(click)="selectResult(item)"
											(keydown.enter)="selectResult(item)"
											(keydown.space)="selectResult(item); $event.preventDefault()"
											(keydown.arrowDown)="focusNextResult($event)"
											(keydown.arrowUp)="focusPreviousResult($event)"
											[attr.aria-label]="getResultAriaLabel(item)"
										>
											<span matListItemTitle>{{ title?.key | transloco: title?.params }}</span>
											<span matListItemLine>{{
												'global_search.assignment_subtitle'
													| transloco
														: {
																number: item.item.guid,
																owner: getAssignmentOwnerName(item),
														  }
											}}</span>
										</mat-list-item>
									} @else {
										<mat-list-item
											class="item result-item"
											role="button"
											tabindex="0"
											(click)="selectResult(item)"
											(keydown.enter)="selectResult(item)"
											(keydown.space)="selectResult(item); $event.preventDefault()"
											(keydown.arrowDown)="focusNextResult($event)"
											(keydown.arrowUp)="focusPreviousResult($event)"
											[attr.aria-label]="getResultAriaLabel(item)"
										>
											<span matListItemTitle>{{ item.item.name }}</span>
											<span matListItemLine>{{ mapPersonTypeToKey(item.personType) | transloco }}</span>
										</mat-list-item>
									}
								}
							</mat-list>

							@if (group.isLoadingMore) {
								<div class="state group-state" role="status" aria-live="polite">
									{{ 'global_search.loading_more' | transloco }}
								</div>
							}
							@if (group.loadMoreError) {
								<div class="state group-state error" role="alert">
									<span>{{ 'global_search.load_more_error' | transloco }}</span>
									<button mat-button type="button" (click)="retryLoadMore(group.category!)">
										{{ 'global_search.retry' | transloco }}
									</button>
								</div>
							}
							@if (group.capReached) {
								<div class="state group-state" role="status" aria-live="polite">
									{{ 'global_search.load_more_cap_reached' | transloco }}
								</div>
							}
						</div>
					</div>
				}
			}
		</div>
	</ng-template>
</div>
